import CloseIcon from '@material-ui/icons/Close';
import LinkIcon from '@material-ui/icons/Link';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { BiUpload } from "react-icons/bi";
import { useDispatch } from "react-redux";
import CustomShowModal from "../../../components/Common/CustomShowModal";
import { toastSuccess } from "../../../utils";
import * as commonActions from "../../Common/actions";
import * as actions from "./actions";


const AddUpdateComplaints = forwardRef((props, ref) => {
  const {
    setLoader = () => { },
    onCloseModal = () => { },
    getAllSavedResults = () => { },
    singleResults = {},
    isEdit = false,
    isAdminView = false,
  } = props;
  const [currentData, setCurrentData] = useState(isEdit ? singleResults : {});
  const {
    fullName = "",
    title = "",
    pageName = "",
    emailAddress = "",
    mobileNumber = "",
    mobileNumber2 = "",
    emailAddress2 = "",
    remarks = "",
    descriptions = "",
    current_status = "",
    images = ["-"]
  } = currentData || {};

  const dispatch = useDispatch();
  const [selectedValue, setSelectedValue] = useState(
    isEdit ? singleResults?.type : "Individual"
  );
  const [errorImages, setErrorImages] = useState([]);
  const fileInputRef = useRef(null);

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const onOpenErrorAlert = (errors = []) => {
    dispatch(commonActions.openErrorAlert({ errorList: errors }));
  };

  useImperativeHandle(ref, () => ({
    async onSubmitQuery() {
      setLoader(true);

      // Create FormData for file upload
      const formData = new FormData();

      // Append all current data fields
      Object.keys(currentData).forEach(key => {
        formData.append(key, currentData[key] || '');
      });

      // Append error images
      errorImages.forEach((file, index) => {
        formData.append('files', file);
      });

      if (isEdit) {
        try {
          // Append ID for update
          let newQuery = {
            ...currentData,
            type: selectedValue,
            individual_gender: currentData?.individual_gender || "male",
          };

          actions
            .updateComplaints(formData)
            .then((res) => {
              toastSuccess("Details Updated successfully");
              onCloseModal();
              getAllSavedResults();
              setLoader(false);
            })
            .catch(() => {
              setLoader(false);
            });
        } catch (ex) {
          setLoader(false);
        }
        return;
      }

      actions
        .addComplaints(formData)
        .then((res) => {
          toastSuccess("Details added successfully");
          onCloseModal();
          getAllSavedResults();
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    },
  }));

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Separate images and videos based on file extensions
  const mediaTypes = useMemo(() => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];

    const sortedMedia = {
      images: [],
      videos: []
    };

    images.forEach(url => {
      const extension = url.substring(url.lastIndexOf('.')).toLowerCase();

      if (imageExtensions.includes(extension)) {
        sortedMedia.images.push(url);
      } else if (videoExtensions.includes(extension)) {
        sortedMedia.videos.push(url);
      }
    });

    return sortedMedia;
  }, [images]);
  const onChangeHandler = (e) => {
    const { value = "", name = "" } = e.target || {};
    setCurrentData({ ...currentData, [name]: value });
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setErrorImages(prevImages => [...prevImages, ...files]);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveImage = (item) => {
    if (typeof item === 'number') {
      setErrorImages((prevImages) => prevImages.filter((_, i) => i !== item));
    }
    // Check if the item is a URL (from mediaTypes.images)
    else if (typeof item === 'string') {
      // Remove from images in currentData
      const updatedImages = images.filter((url) => url !== item);

      setCurrentData((prevData) => ({
        ...prevData,
        images: updatedImages
      }));
    }
  };
  return (
    <div className="w-full text-[11px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
        {/* Left Table: General Details */}
        <div className="border border-[#cbd5e1] bg-white">
          <table className="w-full table-fixed border-collapse text-[11px] text-slate-900">
            <thead>
              <tr className="bg-[#e2e8f0] text-center font-medium text-[#1e293b]">
                <th colSpan={2} className="border border-[#cbd5e1] px-2 py-1.5 text-left font-medium text-[11px]">
                  General Details
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Full Name */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] w-1/3 text-slate-700 font-medium">
                  <label htmlFor="complaint-fullName">Full Name <span className="text-rose-500">*</span></label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="text"
                    id="complaint-fullName"
                    name="fullName"
                    placeholder="Enter Full Name"
                    value={fullName}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* Title */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">
                  <label htmlFor="complaint-title">Title</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="text"
                    id="complaint-title"
                    name="title"
                    placeholder="Enter Error Title"
                    value={title}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* Page Name */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">
                  <label htmlFor="complaint-pageName">Page Name</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="text"
                    id="complaint-pageName"
                    name="pageName"
                    placeholder="Enter Page Name"
                    value={pageName}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* Mobile No (official) */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">
                  <label htmlFor="complaint-mobileNumber">Mobile No. (official)</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="text"
                    id="complaint-mobileNumber"
                    name="mobileNumber"
                    placeholder="Enter Mobile Number"
                    value={mobileNumber}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* Mobile No (official) 2 */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">
                  <label htmlFor="complaint-mobileNumber2">Mobile No. (official) 2</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="text"
                    id="complaint-mobileNumber2"
                    name="mobileNumber2"
                    placeholder="Enter Mobile No (official) 2"
                    value={mobileNumber2}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* Status (Admin View) */}
              {isAdminView && (
                <tr>
                  <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">
                    <label htmlFor="complaint-current_status">Status</label>
                  </td>
                  <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                    <select
                      id="complaint-current_status"
                      name="current_status"
                      value={current_status || ""}
                      onChange={onChangeHandler}
                      className="w-full h-7 px-1 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] font-medium cursor-pointer capitalize"
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </td>
                </tr>
              )}

              {/* Attachments */}
              {!isAdminView && (
                <tr>
                  <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium align-top pt-1.5">
                    Attachments
                  </td>
                  <td className="border border-[#cbd5e1] p-1 bg-white">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current.click()}
                        className="h-7 px-2 bg-[#313C4C] hover:bg-[#1e2632] text-white text-[11px] font-medium uppercase border-none rounded-none flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        <BiUpload size={12} />
                        <span>Upload</span>
                      </button>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        multiple
                        accept="image/*,video/*"
                        onChange={handleFileSelect}
                      />
                      <span className="text-[10px] text-slate-600 font-mono">
                        {errorImages.length} file{errorImages.length !== 1 ? "s" : ""}
                      </span>
                      {(mediaTypes.images.length > 0 || (mediaTypes.videos.length > 0 && current_status !== 'resolved')) && (
                        <button
                          type="button"
                          onClick={handleOpen}
                          className="h-7 px-2 bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 text-[10px] font-medium rounded-none flex items-center gap-1 cursor-pointer transition-colors"
                        >
                          <LinkIcon style={{ fontSize: 13 }} />
                          <span>Media ({mediaTypes.images.length + mediaTypes.videos.length})</span>
                        </button>
                      )}
                    </div>

                    {errorImages.length > 0 && (
                      <div className="mt-1 max-h-[80px] overflow-y-auto scroll-thin border border-[#cbd5e1] bg-slate-50 divide-y divide-slate-200">
                        {errorImages.map((file, index) => (
                          <div key={index} className="flex items-center justify-between px-1.5 py-0.5 text-[10px]">
                            <span className="truncate text-slate-700 max-w-[150px] font-mono">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              aria-label={`Remove ${file.name}`}
                              className="text-rose-600 hover:text-rose-800 text-[10px] border-none bg-transparent cursor-pointer ml-1"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              )}

              {isAdminView && (
                <tr>
                  <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">Attachments</td>
                  <td className="border border-[#cbd5e1] p-1 bg-white">
                    {mediaTypes.images.length + mediaTypes.videos.length > 0 ? (
                      <button
                        type="button"
                        onClick={handleOpen}
                        className="h-7 px-2 bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-[11px] rounded-none inline-flex items-center gap-1.5 transition-colors"
                      >
                        <LinkIcon className="!text-[14px]" />
                        View media ({mediaTypes.images.length + mediaTypes.videos.length})
                      </button>
                    ) : <span className="px-1 text-slate-500">No attachments</span>}
                  </td>
                </tr>
              )}

              {/* Remarks */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium align-top pt-1.5">
                  <label htmlFor="complaint-remarks">Notes / Remarks</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-white">
                  <textarea
                    rows={3}
                    id="complaint-remarks"
                    name="remarks"
                    placeholder="Please enter your remarks here…"
                    value={remarks}
                    onChange={onChangeHandler}
                    className="block w-full min-h-[72px] p-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium resize-y"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Right Table: Contact & Description */}
        <div className="border border-[#cbd5e1] bg-white">
          <table className="w-full table-fixed border-collapse text-[11px] text-slate-900">
            <thead>
              <tr className="bg-[#e2e8f0] text-center font-medium text-[#1e293b]">
                <th colSpan={2} className="border border-[#cbd5e1] px-2 py-1.5 text-left font-medium text-[11px]">
                  Contact & Description
                </th>
              </tr>
            </thead>
            <tbody>
              {/* E-mail (official) */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] w-1/3 text-slate-700 font-medium">
                  <label htmlFor="complaint-emailAddress">Email (official) <span className="text-rose-500">*</span></label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="email"
                    id="complaint-emailAddress"
                    name="emailAddress"
                    placeholder="Enter e-mail (official)"
                    value={emailAddress}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* E-mail (official) 2 */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium">
                  <label htmlFor="complaint-emailAddress2">Email (official) 2</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <input
                    type="email"
                    id="complaint-emailAddress2"
                    name="emailAddress2"
                    placeholder="Enter e-mail (official) 2"
                    value={emailAddress2}
                    onChange={onChangeHandler}
                    className="w-full h-7 px-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium"
                  />
                </td>
              </tr>

              {/* Descriptions */}
              <tr>
                <td className="border border-[#cbd5e1] px-2 py-1 bg-[#f8fafc] text-slate-700 font-medium align-top pt-1.5">
                  <label htmlFor="complaint-descriptions">Description</label>
                </td>
                <td className="border border-[#cbd5e1] p-0 bg-[#ffffd6]">
                  <textarea
                    rows={9}
                    id="complaint-descriptions"
                    name="descriptions"
                    placeholder="Please enter error descriptions here…"
                    value={descriptions}
                    onChange={onChangeHandler}
                    className="block w-full min-h-[72px] p-2 text-[11px] bg-[#ffffd6] text-slate-900 border-none outline-none focus:bg-[#fff9a6] focus:ring-1 focus:ring-inset focus:ring-[#3b82f6] font-medium resize-y"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Review Media Modal */}
      <CustomShowModal
        open={open}
        handleClose={handleClose}
        title="Review Media"
        maxWidth="sm"
        contentClassName="!p-3 max-h-[60vh]"
      >
        {/* Images Section */}
        {mediaTypes.images.length > 0 && (
          <div className="mb-4">
            <h4 className="text-[11px] font-semibold text-slate-800 uppercase tracking-wider mb-2 border-b border-slate-200 pb-1">
              Images ({mediaTypes.images.length})
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {mediaTypes.images.map((url, index) => (
                <div key={index} className="relative border border-[#cbd5e1] bg-slate-50 group">
                  {!isAdminView && current_status !== 'resolved' && (
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(url)}
                      className="absolute top-1 right-1 z-10 bg-rose-600 text-white rounded-full p-0.5 hover:bg-rose-700 border-none cursor-pointer"
                      title="Remove"
                      aria-label={`Remove image ${index + 1}`}
                    >
                      <CloseIcon style={{ fontSize: 12 }} />
                    </button>
                  )}
                  <button type="button" className="w-full cursor-pointer" aria-label={`Open image ${index + 1} in a new tab`} onClick={() => handleOpenInNewTab(url)}>
                    <img src={url} alt={`Image ${index + 1}`} className="w-full h-20 object-cover block" />
                    <span className="block p-1 text-center bg-white text-[10px] text-slate-700 truncate border-t border-slate-200">
                      Image {index + 1}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Videos Section */}
        {mediaTypes.videos.length > 0 && (
          <div>
            <h4 className="text-[11px] font-semibold text-slate-800 uppercase tracking-wider mb-2 border-b border-slate-200 pb-1">
              Videos ({mediaTypes.videos.length})
            </h4>
            <div className="space-y-1.5">
              {mediaTypes.videos.map((url, index) => (
                <div key={index} className="flex items-center justify-between p-2 border border-[#cbd5e1] bg-slate-50">
                  <div className="flex items-center gap-2 truncate">
                    <span className="text-[10px] px-1.5 py-0.5 bg-sky-100 text-sky-800 font-semibold border border-sky-200">
                      Video {index + 1}
                    </span>
                    <span className="text-[11px] text-slate-700 truncate" title={url}>
                      {url.split('/').pop()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenInNewTab(url)}
                      className="h-7 px-2 text-[10px] font-medium text-white bg-[#313C4C] hover:bg-[#1e2632] border border-[#313C4C] rounded-none uppercase cursor-pointer"
                    >
                      Open Link
                    </button>
                    {!isAdminView && current_status !== 'resolved' && (
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(url)}
                        className="text-rose-600 hover:text-rose-800 text-[10px] border-none bg-transparent cursor-pointer"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {mediaTypes.images.length === 0 && mediaTypes.videos.length === 0 && (
          <div className="text-center text-slate-500 py-6 text-[11px]">
            No media available
          </div>
        )}
      </CustomShowModal>
    </div>
  );
});

export default AddUpdateComplaints;
